import type React from "react";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { Data, SummaryResult } from "../../../../bext-output-type";

interface ComparativeChartsProps {
  simulationData: Data;
  hasExtraAmortization?: boolean;
}

const COLORS = ["#0000FF", "#888899", "#AABBCC", "#DDDDDD"];

const formatBrl = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);

const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: any) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  if (percent < 0.05) return null;

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      fontWeight="bold"
    >
      {`${(percent * 100).toFixed(2)}%`}
    </text>
  );
};

// --- Reusable Chart Components ---

interface PieChartCardProps {
  title: string;
  data: { name: string; value: number }[];
}

const CustomPieTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border rounded shadow-sm text-xs">
        <p className="font-bold">{payload[0].name}</p>
        <p>{formatBrl(payload[0].value)}</p>
      </div>
    );
  }
  return null;
};

const PieChartCard: React.FC<PieChartCardProps> = ({ title, data }) => (
  <div className="bg-white border rounded-2xl p-4 shadow-sm">
    <h4 className="text-center font-semibold text-gray-600 mb-4 text-sm uppercase">
      {title}
    </h4>
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Legend
            verticalAlign="top"
            height={36}
            iconType="rect"
            wrapperStyle={{ fontSize: "12px" }}
          />
          <Tooltip content={<CustomPieTooltip />} />
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {data.map((_, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  </div>
);

interface LineChartCardProps {
  title: string;
  data: { installment: number; parcela: number; amortizacaoMensal: number }[];
}

const renderCustomAxisTick = ({ x, y, payload }: any) => (
  <text
    x={x}
    y={y + 10}
    fill="#666"
    textAnchor="end"
    transform={`rotate(-45 ${x} ${y})`}
    fontSize={10}
  >
    {payload.value}
  </text>
);

const CustomLineTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border rounded shadow-sm text-xs">
        <p className="font-bold mb-1">Parcela {label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {formatBrl(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const LineChartCard: React.FC<LineChartCardProps> = ({ title, data }) => (
  <div className="bg-white border rounded-2xl p-4 shadow-sm">
    <h4 className="text-center font-semibold text-gray-600 mb-4 text-sm uppercase">
      {title}
    </h4>
    <div className="h-72 w-full mt-4">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 5, right: 20, left: 10, bottom: 25 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="installment"
            tick={renderCustomAxisTick}
            interval="preserveStartEnd"
            minTickGap={20}
          />
          <YAxis
            tickFormatter={(val) => val.toLocaleString("pt-BR")}
            width={50}
            tick={{ fontSize: 10 }}
          />
          <Tooltip content={<CustomLineTooltip />} />
          <Legend
            verticalAlign="top"
            height={36}
            iconType="rect"
            wrapperStyle={{ fontSize: "12px" }}
          />
          <Line
            type="monotone"
            dataKey="parcela"
            name="Parcela"
            stroke="#0000FF"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="amortizacaoMensal"
            name="Amortização mensal"
            stroke="#888899"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
);

// --- Helpers ---

function createPieData(summary: SummaryResult) {
  return [
    { name: "Financiado", value: summary.loanAmount || 0 },
    { name: "Juros", value: summary.totalInterestAmount || 0 },
    { name: "Taxas/Seguros", value: summary.totalFees || 0 },
    { name: "Correção", value: summary.totalCorrectionValue || 0 },
  ].filter((item) => item.value > 0);
}

function createLineData(
  table: { installment: number; installmentAmountWithFee: number; installmentAmount: number; monthlyAmortization: number; amortization: number }[],
) {
  return table
    .filter((row) => row.installment > 0)
    .map((row) => ({
      installment: row.installment,
      parcela: row.installmentAmountWithFee || row.installmentAmount || 0,
      amortizacaoMensal: (row.monthlyAmortization || 0) + (row.amortization || 0),
    }));
}

// --- Main Component ---

export const ComparativeCharts: React.FC<ComparativeChartsProps> = ({
  simulationData,
  hasExtraAmortization,
}) => {
  const {
    summaryResult,
    summaryWithAmortizationResult,
    simulationTable,
    simulationWithAmortizationTable,
  } = simulationData;

  if (!summaryResult) return null;

  const pieDataSem = createPieData(summaryResult);
  const pieDataCom = summaryWithAmortizationResult
    ? createPieData(summaryWithAmortizationResult)
    : pieDataSem;

  const lineDataSem = createLineData(simulationTable);
  const lineDataCom =
    simulationWithAmortizationTable?.length > 0
      ? createLineData(simulationWithAmortizationTable)
      : lineDataSem;

  return (
    <div className="mt-8 mb-8">
      <h3 className="text-xl font-bold text-gray-800 mb-6 px-2 text-center md:text-left">
        Comparativo Financiamento
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <PieChartCard
          title={
            hasExtraAmortization
              ? "Sem Amortização Extra"
              : "Divisão do Financiamento"
          }
          data={pieDataSem}
        />
        {hasExtraAmortization && (
          <PieChartCard title="Com Amortização Extra" data={pieDataCom} />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <LineChartCard
          title={
            hasExtraAmortization
              ? "Evolução Sem Amortização Extra"
              : "Evolução do Financiamento"
          }
          data={lineDataSem}
        />
        {hasExtraAmortization && (
          <LineChartCard
            title="Evolução Com Amortização Extra"
            data={lineDataCom}
          />
        )}
      </div>
    </div>
  );
};
